import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '@givebutter/primitives/button';
import { faSend } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SendTestMessageDialog } from 'assets/js/pages/engage/components/SendTestMessageDialog';
import { AddNoteActionFormValuesDisplay } from 'assets/js/pages/engage/workflows/actions/AddNote';
import { CreateTaskActionFormValuesDisplay } from 'assets/js/pages/engage/workflows/actions/CreateTask';
import { SetCustomFieldFormValuesDisplay } from 'assets/js/pages/engage/workflows/actions/SetCustomField';
import { TagContactActionFormValuesDisplay } from 'assets/js/pages/engage/workflows/actions/TagContact';
import { SendEmailNodeData, SendSmsNodeData } from 'assets/js/pages/engage/workflows/actions/types';
import { SendEmailActionFormValuesDisplay } from 'assets/js/pages/engage/workflows/components/EmailMessageBuilder';
import { SendSmsActionFormValuesDisplay } from 'assets/js/pages/engage/workflows/components/SmsMessageBuilder';
import { useCustomFieldFilterItems } from 'assets/js/pages/engage/workflows/hooks/useCustomFieldFilterItems';
import { useFindDelayNode } from 'assets/js/pages/engage/workflows/hooks/useFindDelayNode';
import { actionNodeTypes } from 'assets/js/pages/engage/workflows/utils/constants';
import { transformStepsToNodes } from 'assets/js/pages/engage/workflows/utils/transformers';
import { NodeWithData } from 'assets/js/pages/engage/workflows/utils/types';
import { WorkflowActionTypeEnum } from 'constants/workflows';
import { useCurrentWorkflow } from 'customHooks/useCurrentWorkflow';
import { useCurrentWorkflowSteps } from 'customHooks/useCurrentWorkflowSteps';

const EditWorkflowReviewActionRows = () => {
    const history = useHistory();
    const { data: customFieldOptions } = useCustomFieldFilterItems();
    const { data: workflow } = useCurrentWorkflow();
    const { data: workflowSteps } = useCurrentWorkflowSteps({
        version: workflow.has_draft_version
            ? workflow.current_version + 1
            : workflow.current_version,
    });

    const nodes = useMemo(
        () =>
            transformStepsToNodes({
                steps: workflowSteps,
                workflow,
                selectable: false,
                customFieldOptions,
            }),
        [customFieldOptions, workflow, workflowSteps],
    );

    const actionNodes = nodes.filter(n => n.type && actionNodeTypes.includes(n.type));
    if (actionNodes.length <= 0) return;

    return (
        <ul>
            {actionNodes.map(node => {
                return (
                    <li
                        key={node.id}
                        className="flex items-center justify-between gap-8 border-b border-gray-100 px-6 py-4 last:border-0"
                    >
                        <ActionValuesDisplaySwitcher nodes={nodes} nodeId={node.id} />
                        <div className="flex items-center gap-4">
                            <Button
                                variant="transparent"
                                size="sm"
                                onClick={() => history.push('./build', { selectedNodeId: node.id })}
                            >
                                Edit
                            </Button>

                            {node.type === WorkflowActionTypeEnum.SEND_EMAIL && (
                                <SendTestMessageButton
                                    channel="email"
                                    messageId={(node.data as SendEmailNodeData).messageId}
                                />
                            )}

                            {node.type === WorkflowActionTypeEnum.SEND_SMS && (
                                <SendTestMessageButton
                                    channel="sms"
                                    messageId={(node.data as SendSmsNodeData).messageId}
                                />
                            )}
                        </div>
                    </li>
                );
            })}
        </ul>
    );
};

interface ActionValuesDisplaySwitcherProps {
    nodes: NodeWithData[];
    nodeId: string;
}
const ActionValuesDisplaySwitcher = ({ nodes, nodeId }: ActionValuesDisplaySwitcherProps) => {
    const node = nodes.find(n => n.id === nodeId);
    const delayNode = useFindDelayNode(node);

    if (!node?.data?.formData) return null;
    const { type, formData } = node.data;

    switch (type) {
        case WorkflowActionTypeEnum.ADD_NOTE:
            return <AddNoteActionFormValuesDisplay values={formData.values} showLabel />;
        case WorkflowActionTypeEnum.SEND_EMAIL:
            return (
                <SendEmailActionFormValuesDisplay
                    values={formData.values}
                    showLabel
                    delayNode={delayNode}
                />
            );
        case WorkflowActionTypeEnum.SEND_SMS:
            return (
                <SendSmsActionFormValuesDisplay
                    values={formData.values}
                    showLabel
                    delayNode={delayNode}
                />
            );
        case WorkflowActionTypeEnum.TASK_CREATE:
            return <CreateTaskActionFormValuesDisplay values={formData.values} showLabel />;
        case WorkflowActionTypeEnum.SET_CUSTOM_FIELD:
            return <SetCustomFieldFormValuesDisplay values={formData.values} showLabel />;
        case WorkflowActionTypeEnum.CONTACT_TAG:
            return <TagContactActionFormValuesDisplay values={formData.values} showLabel />;
        default:
            return null;
    }
};

interface SendTestMessageButtonProps {
    channel: 'email' | 'sms';
    messageId?: number;
}
const SendTestMessageButton = ({ channel, messageId }: SendTestMessageButtonProps) => {
    if (!messageId) return null;
    return (
        <SendTestMessageDialog channel={channel} messageId={messageId}>
            <Button variant="outline" color="neutral" size="sm">
                <FontAwesomeIcon icon={faSend} size="sm" />
                Send a test
            </Button>
        </SendTestMessageDialog>
    );
};

export { EditWorkflowReviewActionRows };
